import styles from "./ListProductCard.module.css"
import { useRef } from 'react';
import { Users, Calendar } from 'lucide-react';
import { ProductCardProps } from "../../../types/product.types";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import { getPrimaryProductImage } from "../../../services/productImageService";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useOrderContext } from "../../../context/OrderContext";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { getPeopleWord } from "../../../utils/people";
import { normalizeDate, denormalizeDate, getDayWord } from "../../../utils/calendar";
import Tooltip from "../../Tooltip/Tooltip";
import { useState } from "react";
import { defaultOrderTime } from "../../../constants/times";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import useCardClickGuard from "../../../hooks/useCardClickGuard";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";
/**
* List card for products
*
* @param props ProductCardProps
* @param {Product} props.product - Product
* @returns TSX.Element
*/
const ListProductCard = ({ product, type = "sales" }: ProductCardProps) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const { orderData } = useOrderContext(); // Order data
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const {isPublicView} = usePublicCatalog();

    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: type,
            dateKey,
            dateISO,
        },
    });
    const buttonRef = useRef<HTMLDivElement>(null)
    const advance = getAdvanceDays(product);
    const orderAdvanceLabel =
        i18n.language === "en" ? advance : product.order_in_advance ?? "N/A";

    // Handle opening detail
    const handleOpen = useCardClickGuard([buttonRef], () => {
        openDetail && openDetail({id: product.id, type: type }); 
    });
    // Sample time limit
    const { min: minDelivery, max: maxDelivery } = getDeliveryWindow(product);
    // Wording for days in advance
    const dayWord = getDayWord(i18n.language, advance);
    // Wording for recommended amount of people
    const peopleWord = getPeopleWord(i18n.language, product.max_participants ?? product.min_participants ?? 1)

    // Make sure price is always in correct currency
    const no_dph_price =
        product.pricing?.price_without_tax != null
            ? czk_to_eur(product.pricing.price_without_tax, czk_eur_rate, selectedCurrency)
            : "N/A";
    const dph_price =
        product.pricing?.price_with_tax != null
            ? czk_to_eur(product.pricing.price_with_tax, czk_eur_rate, selectedCurrency)
            : "N/A";
    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "product",
    });

    const [showTooltip, setShowTooltip] = useState(false);
    
    return(
        <>
            <div className={styles.ProductCard} key={product.id} onClick={handleOpen} >
                <div className={styles.ProductCardImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
                <div className={styles.ProductCardContent}>
                    <div className={styles.CardContentTop}>
                        <div className={styles.ProductTitle}>
                            <p>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                        </div>
                        <div className={styles.ProductPrice}>
                            {product.pricing?.price_without_tax != null && (
                                <p className={styles.DPH}>{formatCurrency(no_dph_price, selectedCurrency)} <Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                            )}
                            {product.pricing?.price_with_tax != null && (
                                <p className={styles.NoDPH}>{formatCurrency(dph_price, selectedCurrency)} {t("products.dph")}</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.CardContentBottom}>
                        <div className={styles.ProductSpecs}>
                            <div className={styles.ProductSpec}>
                                <div className={styles.SpecIcon}>
                                    <Calendar/>
                                </div>
                                <div className={styles.SpecText}>
                                    <p>{t("products.order")} {orderAdvanceLabel} {dayWord} {t("products.before")}</p>
                                </div>
                            </div>
                            <div className={styles.ProductSpec}>
                                <div className={styles.SpecIcon}>
                                    <Users/>
                                </div>
                                <div className={styles.SpecText}>
                                    <p>
                                        {t("products.good_for")} {((product.min_participants !== null && product.min_participants !== 0) ? product.min_participants : "1 ")}
                                        {product.max_participants !== null && product.max_participants !== product.min_participants
                                            ? ` - ${product.max_participants} `
                                            : " "} 
                                        {(product.min_participants === 0 && (product.max_participants === 0 || product.max_participants === null)) ? getPeopleWord(i18n.language, 1) : peopleWord}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={styles.CartButtons} ref={buttonRef}>
                            {(cartAmount >0 && !isPublicView) ? (
                                < div className = {styles.Amount} onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} >
                                    <CartAmountSecondary amountInCart={cartAmount} 
                                        disabledIncrease={cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)}
                                        onIncrease={() => handleCart(cartAmount+(1*(product.granularity??1)))}
                                        onDecrease={()=>{cartAmount > product.min_order ? handleCart(cartAmount - (1*(product.granularity??1))) : handleCart(0)}}
                                        className={styles.CartAmount} disabled={isPublicView} />
                                    {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={buttonRef}>{tooltip}</Tooltip>}
                                </div>
                            ) : (
                                <CartButtonSecondary
                                    text={t("buttons.order")}
                                    onClick={() =>
                                        openTimeModal({
                                            type: "single",
                                            date: orderData.selectedDate,
                                            time: defaultOrderTime,
                                            productId: product.id,
                                            prodType: type,
                                            static: true,
                                            amount: Math.max(product.min_order, product.granularity) ?? 1,
                                            restrictions: { min: minDelivery, max: maxDelivery },
                                            advance: advance,
                                        })
                                    }
                                    className={styles.CartButton}
                                    disabled={isPublicView}
                                />
                            )}
                        </div>
                    </div>  
                </div>
            </div>
        </>
    )
}
export default ListProductCard
